import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, FadeUp, Eyebrow } from "@/components/site/Reveal";
import { useProducts } from "@/context/ProductsContext";

const statsFor = (count) => [
  { k: "1997", v: "Year established" },
  { k: "100 kg", v: "Minimum order" },
  { k: String(count), v: "Ink systems" },
  { k: "Bulk", v: "Supply at fair prices" },
];

const GALLERY = ["/img/im-ldpe.jpg", "/img/tide-reverse.jpg", "/img/tide-gravure.jpg"];

export const Heritage = () => {
  const ref = useRef(null);
  const STATS = statsFor(useProducts().products.length);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <section ref={ref} data-testid="heritage-section" className="relative border-t border-black/10 bg-[#EFEDE6]">
      <div className="mx-auto grid max-w-[1440px] gap-16 px-5 py-24 md:grid-cols-12 md:px-10 md:py-36">
        <div className="md:col-span-6">
          <Eyebrow>Since 1997 · Dada Nagar, Kanpur</Eyebrow>
          <h2 className="font-wide mt-6 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl lg:text-6xl">
            <MaskLines onView lines={["Resin.", "Pigment.", <span key="s" className="text-[#FF5A00]">Solvent.</span>]} />
          </h2>
          <FadeUp className="mt-8 max-w-lg space-y-4 text-base leading-relaxed text-zinc-700">
            <p>We make our inks from the finest quality raw materials — resins, pigments and solvents — in a production facility set up for exactly this work.</p>
            <p>Our customers are the printing and packaging industries. Minimum order is 100 kg, and we supply bulk quantities at reasonable prices.</p>
          </FadeUp>
          <div className="mt-12 grid grid-cols-2 border-l border-t border-black/15">
            {STATS.map((s, i) => (
              <FadeUp key={s.v} delay={i * 0.08} className="border-b border-r border-black/15 p-5 md:p-7" data-testid={`stat-${i}`}>
                <p className="font-wide text-3xl font-black tracking-tight md:text-5xl">{s.k}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-700">{s.v}</p>
              </FadeUp>
            ))}
          </div>
        </div>
        <div className="relative grid grid-cols-2 gap-4 md:col-span-6 md:pl-10">
          <motion.div style={{ y: y1 }} className="col-span-2 aspect-[16/10] overflow-hidden bg-white [clip-path:polygon(0_0,100%_0,100%_100%,12%_100%,0_82%)]">
            <img src={GALLERY[0]} alt="Coloured inks pouring" loading="lazy" className="h-full w-full object-contain p-6" />
          </motion.div>
          <motion.div style={{ y: y2 }} className="aspect-square overflow-hidden bg-white">
            <img src={GALLERY[1]} alt="Ink buckets" loading="lazy" className="h-full w-full object-contain p-4" />
          </motion.div>
          <motion.div style={{ y: y1 }} className="aspect-square overflow-hidden rounded-full bg-[#FFC400]">
            <img src={GALLERY[2]} alt="Gravure ink bottles" loading="lazy" className="h-full w-full object-cover mix-blend-multiply" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
